/**
 * T-shirt product section — option selection, price/button sync, gallery dots.
 * Add to cart is handled by the theme's <product-form> element.
 */
(function () {
  function init(root) {
    if (!root || root.dataset.tpReady === 'true') return;
    root.dataset.tpReady = 'true';

    const form = root.querySelector('form');
    const variantInput = root.querySelector('[data-tp-variant-id]');
    const submit = root.querySelector('[data-tp-submit]');
    const submitLabel = root.querySelector('[data-tp-submit-label]');
    const priceEl = root.querySelector('[data-tp-price]');
    const colorName = root.querySelector('[data-tp-color-name]');
    const list = root.querySelector('[data-tp-media-list]');
    const dots = Array.from(root.querySelectorAll('[data-tp-dot]'));
    const groups = Array.from(root.querySelectorAll('[data-tp-option]'));

    const variants = (() => {
      try {
        return JSON.parse(root.querySelector('[data-tp-variants]')?.textContent || '[]');
      } catch (e) {
        return [];
      }
    })();

    const moneyFormat = root.dataset.tpMoneyFormat || '${{amount}}';
    const addLabel = root.dataset.tpAddLabel || 'Add to cart';
    const soldOutLabel = root.dataset.tpSoldOutLabel || 'Sold out';

    function formatMoney(cents) {
      // Whole amounts drop the cents, matching money_without_trailing_zeros in Liquid.
      const amount = (Number(cents) / 100).toFixed(2).replace(/\.00$/, '');
      return moneyFormat
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(cents) / 100)))
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }

    function selectedValues() {
      return groups.map((group) => {
        const checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    function findVariant(values) {
      return variants.find((v) => v.options.every((o, i) => values[i] === o));
    }

    function markUnavailable() {
      const values = selectedValues();
      groups.forEach((group, gi) => {
        group.querySelectorAll('input').forEach((input) => {
          const test = values.slice();
          test[gi] = input.value;
          const match = findVariant(test);
          input.dataset.unavailable = match && match.available ? 'false' : 'true';
        });
      });
    }

    function scrollToMedia(mediaId) {
      if (!list || !mediaId) return;
      const target = list.querySelector('[data-media-id="' + mediaId + '"]');
      if (!target) return;
      if (window.matchMedia('(min-width: 990px)').matches) {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        list.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      }
    }

    function update(fromUser) {
      const values = selectedValues();
      const variant = findVariant(values);
      markUnavailable();
      if (!variant) {
        if (submit) {
          submit.disabled = true;
          if (submitLabel) submitLabel.textContent = soldOutLabel;
        }
        return;
      }

      if (variantInput) {
        variantInput.value = String(variant.id);
        variantInput.disabled = !variant.available;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (priceEl) {
        priceEl.innerHTML =
          variant.compare_at_price > variant.price
            ? '<s>' + formatMoney(variant.compare_at_price) + '</s>' + formatMoney(variant.price)
            : formatMoney(variant.price);
      }

      if (submit) {
        submit.disabled = !variant.available;
        submit.removeAttribute('aria-disabled');
        if (submitLabel) submitLabel.textContent = variant.available ? addLabel : soldOutLabel;
      }

      groups.forEach((group) => {
        const out = group.querySelector('[data-tp-selected]');
        const checked = group.querySelector('input:checked');
        if (out && checked) out.textContent = checked.dataset.label || checked.value;
      });

      if (colorName) {
        const colorGroup = groups.find((g) => g.dataset.tpColor === 'true');
        const checked = colorGroup && colorGroup.querySelector('input:checked');
        if (checked) colorName.textContent = 'in ' + checked.value;
      }

      if (fromUser) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        if (variant.featured_media_id) scrollToMedia(variant.featured_media_id);
      }
    }

    groups.forEach((group) => {
      group.addEventListener('change', () => update(true));
    });

    if (list && dots.length) {
      let raf = 0;
      list.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const width = list.clientWidth || 1;
            const index = Math.round(list.scrollLeft / width);
            dots.forEach((d, i) => d.setAttribute('aria-current', i === index ? 'true' : 'false'));
          });
        },
        { passive: true }
      );
    }

    form?.addEventListener('submit', (event) => {
      if (!variantInput?.value || variantInput.disabled) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    });

    update(false);
    // <product-form> re-enables the variant input when it connects; re-apply our state after.
    queueMicrotask(() => update(false));
    setTimeout(() => update(false), 0);
  }

  function boot() {
    document.querySelectorAll('[data-tp]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector?.('[data-tp]');
    if (root) {
      delete root.dataset.tpReady;
      init(root);
    }
  });
})();
